import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Image, Row, Stack } from "react-bootstrap";
import { APP_RUNTIME_CELEBRATIONS } from "../../../_CORE/_RuntimeConfig/Runtime/appRuntime.generated";
import {
  applyCelebrationBranding, applyUploadedBranding, getActiveBranding,
  restoreDefaultBranding, uploadCelebrationBranding,
} from "../../../_CORE/_RuntimeConfig/Branding/celebrationBrandingRenderer";

export default function CelebrationBrandingTestPanel() {
  const memo__celebrationOptionsMemo = useMemo(() => APP_RUNTIME_CELEBRATIONS.map((item) => ({
    value: item.id, label: item.shortName || item.name || item.id,
  })), []);

  const [useNameCelebrationIdState, setNameCelebrationIdState] = useState(memo__celebrationOptionsMemo[0]?.value || "");
  const [useNameLogoState, setNameLogoState] = useState(null);
  const [useNameFaviconState, setNameFaviconState] = useState(null);
  const [useNameHeaderImageState, setNameHeaderImageState] = useState(null);
  const [useNameSocialImageState, setNameSocialImageState] = useState(null);
  const [useNameBrandingState, setNameBrandingState] = useState(getActiveBranding());
  const [useNameMessageState, setNameMessageState] = useState("");
  const [useNameVariantState, setNameVariantState] = useState("info");

  const memo__filesMemo = useMemo(() => ({
    logo: useNameLogoState, favicon: useNameFaviconState,
    headerImage: useNameHeaderImageState, socialImage: useNameSocialImageState,
  }), [useNameLogoState, useNameFaviconState, useNameHeaderImageState, useNameSocialImageState]);

  const cb__showMessageCallback = useCallback((text, type = "info") => {
    setNameMessageState(text);
    setNameVariantState(type);
  }, []);

  const cb__applySelectedCelebrationCallback = useCallback(async () => {
    if (!useNameCelebrationIdState) return;

    try {
      const branding = await applyCelebrationBranding(useNameCelebrationIdState);
      setNameBrandingState(branding);
      cb__showMessageCallback(`Celebración ${useNameCelebrationIdState} aplicada.`, "success");
    } catch (error) {
      cb__showMessageCallback(error.message, "danger");
    }
  }, [useNameCelebrationIdState, cb__showMessageCallback]);

  const cb__testUploadedAssetsCallback = useCallback(() => {
    setNameBrandingState(applyUploadedBranding(memo__filesMemo));
    cb__showMessageCallback("Archivos temporales aplicados.", "success");
  }, [memo__filesMemo, cb__showMessageCallback]);

  const cb__restoreCompanyBrandingCallback = useCallback(() => {
    setNameBrandingState(restoreDefaultBranding());
    cb__showMessageCallback("Branding de empresa restaurado.", "secondary");
  }, [cb__showMessageCallback]);

  const cb__uploadToDatabaseCallback = useCallback(async () => {
    try {
      await uploadCelebrationBranding(useNameCelebrationIdState, memo__filesMemo);
      cb__showMessageCallback(`Branding ${useNameCelebrationIdState} enviado a la base de datos.`, "success");
    } catch (error) {
      cb__showMessageCallback(error.message, "danger");
    }
  }, [useNameCelebrationIdState, memo__filesMemo, cb__showMessageCallback]);

  useEffect(() => {
    const cb__brandingChangeCallback = (event) => setNameBrandingState(event.detail);
    window.addEventListener("app-branding-change", cb__brandingChangeCallback);
    return () => window.removeEventListener("app-branding-change", cb__brandingChangeCallback);
  }, []);

  useEffect(() => {
    if (!useNameCelebrationIdState) return;
    cb__applySelectedCelebrationCallback();
  }, [useNameCelebrationIdState, cb__applySelectedCelebrationCallback]);

  return (
    <Container fluid="lg" className="py-4">
      <Stack gap={4}>
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <h1 className="h3 mb-0">Prueba de branding por celebración</h1>
            <Badge bg="warning" text="dark">TEST</Badge>
          </div>
          <p className="text-secondary mb-0">Las opciones salen directamente de la configuración de celebraciones.</p>
        </div>

        {useNameMessageState ? <Alert variant={useNameVariantState} className="mb-0">{useNameMessageState}</Alert> : null}

        <Row className="g-4">
          <Col lg={7}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <Card.Title className="mb-3">Branding activo</Card.Title>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="text-secondary">Logo dark</Form.Label>
                    <div className="border rounded-3 p-3 text-center bg-dark-subtle">
                      <Image src={useNameBrandingState.logoDark} alt="Logo dark" fluid style={{ maxHeight: 120, objectFit: "contain" }} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="text-secondary">Logo light</Form.Label>
                    <div className="border rounded-3 p-3 text-center bg-light">
                      <Image src={useNameBrandingState.logoLight} alt="Logo light" fluid style={{ maxHeight: 120, objectFit: "contain" }} />
                    </div>
                  </Col>
                  <Col xs={12}>
                    <Form.Label className="text-secondary">Header</Form.Label>
                    <div className="border rounded-3 p-2 text-center bg-body-tertiary min-vh-0">
                      {useNameBrandingState.headerImage ? (
                        <Image src={useNameBrandingState.headerImage} alt="Header" fluid style={{ maxHeight: 220, objectFit: "contain" }} />
                      ) : <div className="py-5 text-secondary">Sin header</div>}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={5}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <Card.Title className="mb-3">Seleccionar celebración</Card.Title>
                <Form.Group className="mb-4">
                  <Form.Label>Celebración</Form.Label>
                  <Form.Select value={useNameCelebrationIdState} onChange={(event) => setNameCelebrationIdState(event.target.value)}>
                    {memo__celebrationOptionsMemo.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Stack gap={2}>
                  <Button onClick={cb__applySelectedCelebrationCallback}>Aplicar celebración</Button>
                  <Button variant="outline-secondary" onClick={cb__restoreCompanyBrandingCallback}>Restaurar empresa</Button>
                </Stack>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="shadow-sm border-0">
          <Card.Body>
            <Card.Title className="mb-3">Probar archivos / preparar carga a BD</Card.Title>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Logo</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={(event) => setNameLogoState(event.target.files?.[0] || null)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Favicon</Form.Label>
                  <Form.Control type="file" accept="image/*,.ico" onChange={(event) => setNameFaviconState(event.target.files?.[0] || null)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Header</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={(event) => setNameHeaderImageState(event.target.files?.[0] || null)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Social / Open Graph</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={(event) => setNameSocialImageState(event.target.files?.[0] || null)} />
                </Form.Group>
              </Col>
            </Row>

            <Stack direction="horizontal" gap={2} className="flex-wrap mt-4">
              <Button variant="success" onClick={cb__testUploadedAssetsCallback}>Aplicar prueba temporal</Button>
              <Button variant="outline-primary" onClick={cb__uploadToDatabaseCallback}>Subir a base de datos</Button>
            </Stack>
          </Card.Body>
        </Card>
      </Stack>
    </Container>
  );
}
